/**
 * State Heatmap Component
 * Displays heatmap for cities/counties within a selected state
 */

import React from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';

interface CityData {
  city: string;
  county?: string;
  taskDensity: number;
  tasks: number;
  lat?: number;
  lng?: number;
}

interface StateHeatmapProps {
  state: string;
  stateCode: string;
  cities: CityData[];
  onClose?: () => void;
}

// Get state GeoJSON URL - using counties for more detailed view
const getStateGeoUrl = (stateCode: string) => {
  // For now, we'll use a generic approach
  // In production, you'd want to load specific state county data
  return `https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json`;
};

const StateHeatmap: React.FC<StateHeatmapProps> = ({ state, stateCode, cities, onClose }) => {
  const theme = useTheme();

  const getDensityColor = (density: number) => {
    if (density >= 80) {
      return theme.palette.error.main;
    } else if (density >= 60) {
      return theme.palette.warning.main;
    } else if (density >= 40) {
      return theme.palette.info.main;
    } else {
      return theme.palette.success.main;
    }
  };

  const getMarkerSize = (tasks: number) => {
    if (tasks >= 100) return 12;
    if (tasks >= 50) return 8;
    if (tasks >= 20) return 6;
    return 4;
  };

  // Calculate center coordinates for the state (approximate)
  const stateCenters: Record<string, [number, number]> = {
    CA: [-119.4179, 36.7783],
    TX: [-99.9018, 31.9686],
    FL: [-81.5158, 27.6648],
    NY: [-74.9481, 42.1657],
    MI: [-84.5467, 43.3266],
    PA: [-77.1945, 41.2033],
    IL: [-89.3985, 40.3495],
    OH: [-82.7649, 40.3888],
  };

  const center = stateCenters[stateCode] || [-95.7129, 37.0902]; // Default to US center

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(2),
          padding: theme.spacing(1),
          backgroundColor: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: theme.typography.h6.fontWeight,
            color: theme.palette.text.primary,
            fontSize: theme.typography.body1.fontSize,
          }}
        >
          {state} - City Task Density
        </Typography>
        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Heatmap Map */}
      <Box
        sx={{
          height: 400,
          width: '100%',
          border: `1px solid ${theme.customColors.tableBorder}`,
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{
            scale: 1200,
            center: center,
          }}
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <ZoomableGroup center={center} zoom={4}>
            {/* State outline - simplified, showing all states but highlighting selected */}
            <Geographies geography={getStateGeoUrl(stateCode)}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const code = geo.properties.name || geo.properties.NAME || geo.properties.abbrev || geo.properties.postal;
                  const isSelected = code === stateCode;
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={isSelected ? 'transparent' : theme.palette.background.default}
                      fillOpacity={isSelected ? 0 : 0.3}
                      stroke={isSelected ? theme.palette.primary.main : theme.customColors.tableBorder}
                      strokeWidth={isSelected ? 2 : 0.5}
                    />
                  );
                })
              }
            </Geographies>

            {/* City markers */}
            {cities.map((city, index) => {
              // Use provided coordinates or generate approximate ones
              let lat = city.lat;
              let lng = city.lng;

              if (!lat || !lng) {
                // Generate approximate coordinates within state bounds
                const baseLat = center[1];
                const baseLng = center[0];
                lat = baseLat + (Math.random() - 0.5) * 2;
                lng = baseLng + (Math.random() - 0.5) * 3;
              }

              const color = getDensityColor(city.taskDensity);
              const size = getMarkerSize(city.tasks);

              return (
                <Marker key={index} coordinates={[lng, lat]}>
                  <circle
                    r={size}
                    fill={color}
                    fillOpacity={0.7}
                    stroke={theme.palette.common.white}
                    strokeWidth={1}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.setAttribute('r', String(size + 2));
                      e.currentTarget.setAttribute('fillOpacity', '0.9');
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.setAttribute('r', String(size));
                      e.currentTarget.setAttribute('fillOpacity', '0.7');
                    }}
                  >
                    <title>
                      {city.city}
                      {city.county ? `, ${city.county}` : ''}
                      {'\n'}Tasks: {city.tasks}
                      {'\n'}Density: {city.taskDensity}%
                    </title>
                  </circle>
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
      </Box>

      {/* City List */}
      <Box
        sx={{
          marginTop: theme.spacing(2),
          maxHeight: 200,
          overflowY: 'auto',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: theme.typography.h6.fontWeight,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
            fontSize: theme.typography.body2.fontSize,
          }}
        >
          Top Cities
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
          }}
        >
          {cities
            .sort((a, b) => b.tasks - a.tasks)
            .slice(0, 15)
            .map((city, index) => {
              const color = getDensityColor(city.taskDensity);
              return (
                <Chip
                  key={index}
                  label={`${city.city}: ${city.tasks} tasks`}
                  size="small"
                  sx={{
                    backgroundColor: `${color}33`,
                    color: theme.palette.text.primary,
                    border: `1px solid ${color}`,
                    fontSize: theme.typography.caption.fontSize,
                    height: 24,
                  }}
                />
              );
            })}
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: theme.spacing(2),
          padding: theme.spacing(1.5),
          backgroundColor: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: theme.typography.h6.fontWeight,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(0.5),
              display: 'block',
              fontSize: '0.7rem',
            }}
          >
            Density
          </Typography>
          <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: theme.palette.success.main,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                Low
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: theme.palette.info.main,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                Med
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: theme.palette.warning.main,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                High
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: theme.palette.error.main,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                Very High
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: theme.typography.h6.fontWeight,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(0.5),
              display: 'block',
              fontSize: '0.7rem',
            }}
          >
            Marker Size
          </Typography>
          <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  backgroundColor: theme.palette.text.secondary,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                &lt;20
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  backgroundColor: theme.palette.text.secondary,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                20-50
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  backgroundColor: theme.palette.text.secondary,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                50-100
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: theme.palette.text.secondary,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize }}>
                100+
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StateHeatmap;

